import React, { useState, useEffect } from 'react';
import Modal from '@components/ui/Modal';
import { RiHashtag } from '@remixicon/react';

const MIN_LENGTH = 4;

/**
 * QuoteSearchByQuoteNumberModal
 * Lets the broker type a partial trip number (e.g. "TR-1786…" or the
 * trailing suffix). Case-insensitive partial-match is enforced on the
 * server via the `quote_number` filter on /quotes — the DB column is
 * still called quote_number, only the surface label is "Trip No."
 *
 * On confirm, calls onSelect(query, label).
 *
 * The MIN_LENGTH guard exists because "TR-" (3 chars) prefixes every
 * trip in the org, so a 3-char search matches everything → useless.
 * 4 is the smallest input where the broker has committed at least one
 * meaningful character beyond the prefix.
 */
const QuoteSearchByQuoteNumberModal = ({ isOpen, onClose, onSelect, initialQuery = '' }) => {
  const [value, setValue] = useState(initialQuery);

  useEffect(() => {
    if (isOpen) setValue(initialQuery || '');
  }, [isOpen, initialQuery]);

  const trimmed = value.trim();
  const canApply = trimmed.length >= MIN_LENGTH;

  const handleConfirm = () => {
    if (!canApply) return;
    onSelect(trimmed, `Trip No.: ${trimmed}`);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Search by Trip No." size="sm">
      <div className="p-6 space-y-4">
        <p className="text-sm text-gray-500">
          Show trips whose number contains the entered value. Case-insensitive
          partial match — e.g. <code className="px-1 py-0.5 rounded bg-gray-100 text-xs">TR-1786</code>
          {' '}or the 3-digit suffix.
        </p>
        <div className="relative">
          <RiHashtag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canApply) handleConfirm();
            }}
            placeholder={`Enter trip number… (min ${MIN_LENGTH} chars)`}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoFocus
          />
        </div>
        {trimmed.length > 0 && trimmed.length < MIN_LENGTH && (
          <p className="text-xs text-amber-700">
            Enter at least {MIN_LENGTH} characters — shorter strings match too many trips.
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canApply}
            className="px-4 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      </div>
    </Modal>
  );
};

QuoteSearchByQuoteNumberModal.MIN_LENGTH = MIN_LENGTH;

export default QuoteSearchByQuoteNumberModal;
